/**
 * useNotificationFeed — feed centro notifiche con realtime + paginazione
 * Il Diario di Bordo - Frontend
 *
 * Esposto come **Context Provider singleton**:
 * - 1 sola fetch iniziale + 1 conteggio unread
 * - 1 sola sottoscrizione agli eventi Socket.IO
 * - Tutti i componenti che leggono il feed (NotificationBell, NotificationsPage)
 *   condividono lo stesso state
 *
 * I filtri sono **client-side** sull'array `items` (vedi NotificationsPage):
 * questo è un tradeoff per evitare doppi fetch quando l'utente cambia tab.
 */

import React, { createContext, useContext, useEffect, useState, useCallback, useRef } from 'react'
import api from '../services/api'
import { useSocket, useSocketEvent } from './useSocket'
import { useAuth } from '../contexts/AuthContext'

const PAGE_SIZE = 30

const NotificationFeedContext = createContext(null)

/**
 * Provider del feed notifiche.
 * Da montare DENTRO l'area autenticata (richiede sessione + Socket.IO disponibili).
 *
 * Props:
 *  - autoLoad (default true): se false non carica al mount; usare refresh() esplicito
 */
export function NotificationFeedProvider({ children, autoLoad = true }) {
  const { isAuthenticated } = useAuth()
  const [items, setItems] = useState([])
  const [unreadCount, setUnreadCount] = useState(0)
  const [loading, setLoading] = useState(false)
  const [hasMore, setHasMore] = useState(true)
  const [error, setError] = useState(null)
  const nextBeforeRef = useRef(null)

  const loadInitial = useCallback(async () => {
    if (!isAuthenticated) return
    setLoading(true)
    setError(null)
    try {
      const { data } = await api.get('/notifications', { params: { limit: PAGE_SIZE } })
      const list = data?.data || []
      setItems(list)
      nextBeforeRef.current = data?.pagination?.nextBefore || null
      setHasMore(Boolean(data?.pagination?.nextBefore))
    } catch (err) {
      setError(err.message)
    } finally {
      setLoading(false)
    }
  }, [isAuthenticated])

  const loadMore = useCallback(async () => {
    if (!nextBeforeRef.current || loading || !isAuthenticated) return
    setLoading(true)
    try {
      const { data } = await api.get('/notifications', {
        params: { limit: PAGE_SIZE, before: nextBeforeRef.current }
      })
      const list = data?.data || []
      setItems((prev) => [...prev, ...list])
      nextBeforeRef.current = data?.pagination?.nextBefore || null
      setHasMore(Boolean(data?.pagination?.nextBefore))
    } catch (err) {
      setError(err.message)
    } finally {
      setLoading(false)
    }
  }, [loading, isAuthenticated])

  const loadUnreadCount = useCallback(async () => {
    if (!isAuthenticated) return
    try {
      const { data } = await api.get('/notifications/count')
      setUnreadCount(data?.data?.unread || 0)
    } catch {
      // silent
    }
  }, [isAuthenticated])

  const markRead = useCallback(async (id) => {
    setItems((prev) => prev.map((n) => n._id === id ? { ...n, read: true, readAt: new Date().toISOString() } : n))
    setUnreadCount((c) => Math.max(0, c - 1))
    try {
      await api.put(`/notifications/${id}/read`)
    } catch {
      loadUnreadCount() // rollback minimo: re-fetch count
    }
  }, [loadUnreadCount])

  const dismiss = useCallback(async (id) => {
    setItems((prev) => prev.filter((n) => n._id !== id))
    try {
      await api.put(`/notifications/${id}/dismiss`)
      loadUnreadCount()
    } catch {
      loadUnreadCount()
    }
  }, [loadUnreadCount])

  const markAllRead = useCallback(async () => {
    setItems((prev) => prev.map((n) => ({ ...n, read: true })))
    setUnreadCount(0)
    try {
      await api.put('/notifications/read-all')
    } catch {
      loadUnreadCount()
    }
  }, [loadUnreadCount])

  // Realtime updates (1 sola sottoscrizione globale, anziché una per componente)
  useSocketEvent('notification:new', (notification) => {
    setItems((prev) => {
      // Idempotente: evita duplicati se SOA invia stesso event 2 volte
      if (prev.some((n) => n._id === notification._id)) return prev
      return [notification, ...prev]
    })
    if (!notification.read) setUnreadCount((c) => c + 1)
  }, { enabled: isAuthenticated })

  useSocketEvent('notification:read', ({ _id }) => {
    setItems((prev) => prev.map((n) => n._id === _id ? { ...n, read: true } : n))
    loadUnreadCount()
  }, { enabled: isAuthenticated })

  useSocketEvent('notification:dismiss', ({ _id }) => {
    setItems((prev) => prev.filter((n) => n._id !== _id))
    loadUnreadCount()
  }, { enabled: isAuthenticated })

  useSocketEvent('notification:read-all', () => {
    setItems((prev) => prev.map((n) => ({ ...n, read: true })))
    setUnreadCount(0)
  }, { enabled: isAuthenticated })

  // Emesso dal backend quando l'utente apre una chat: tutte le notifiche
  // message/mention relative a quel thread vengono marcate come read.
  // Aggiorna sia gli items locali che il badge globale.
  useSocketEvent('notification:thread-read', ({ threadId }) => {
    const tid = String(threadId)
    setItems((prev) => prev.map((n) => {
      const itemTid = n.payload?.threadId
      if (itemTid && String(itemTid) === tid && !n.read) {
        return { ...n, read: true, readAt: new Date().toISOString() }
      }
      return n
    }))
    // Ricarica count dal backend per riallineare il badge (source of truth)
    loadUnreadCount()
  }, { enabled: isAuthenticated })

  // Load iniziale solo se autenticato e autoLoad attivo
  useEffect(() => {
    if (!autoLoad || !isAuthenticated) return
    loadInitial()
    loadUnreadCount()
  }, [autoLoad, isAuthenticated, loadInitial, loadUnreadCount])

  // Re-sync su riconnessione WS o ritorno foreground.
  // Su Android (PWA in background), il browser sospende il WebSocket dopo
  // qualche minuto: gli eventi notification:new arrivati in quel frangente
  // vengono persi. Quando il tab torna visibile (o il WS si riconnette)
  // facciamo un fetch fresco di feed + count dal backend per riallineare.
  const { socket } = useSocket()
  useEffect(() => {
    if (!isAuthenticated || !socket) return
    const onConnect = () => {
      loadUnreadCount()
      loadInitial()
    }
    socket.on('connect', onConnect)
    return () => { socket.off('connect', onConnect) }
  }, [socket, isAuthenticated, loadInitial, loadUnreadCount])

  useEffect(() => {
    if (!isAuthenticated) return
    const onVisibility = () => {
      if (document.visibilityState === 'visible') {
        loadUnreadCount()
        loadInitial()
      }
    }
    document.addEventListener('visibilitychange', onVisibility)
    return () => { document.removeEventListener('visibilitychange', onVisibility) }
  }, [isAuthenticated, loadInitial, loadUnreadCount])

  // Reset al logout
  useEffect(() => {
    if (!isAuthenticated) {
      setItems([])
      setUnreadCount(0)
      setHasMore(true)
      nextBeforeRef.current = null
    }
  }, [isAuthenticated])

  const value = {
    items,
    unreadCount,
    loading,
    hasMore,
    error,
    loadMore,
    refresh: loadInitial,
    markRead,
    dismiss,
    markAllRead,
    refreshUnreadCount: loadUnreadCount
  }

  // Usiamo React.createElement perché questo file è .js (no JSX runtime).
  // L'alternativa sarebbe rinominare in .jsx ma rompe import esistenti.
  return React.createElement(
    NotificationFeedContext.Provider,
    { value },
    children
  )
}

/**
 * Hook per leggere il feed notifiche dal Provider.
 * DEVE essere usato dentro <NotificationFeedProvider>.
 *
 * Backwards-compatible: l'argomento `{ filters }` viene ignorato
 * (i filtri sono client-side ora) — è mantenuto solo per non rompere
 * call site esistenti.
 */
export function useNotificationFeed() {
  const ctx = useContext(NotificationFeedContext)
  if (!ctx) {
    throw new Error('useNotificationFeed deve essere usato dentro <NotificationFeedProvider>')
  }
  return ctx
}
